import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CircleProvider } from './contexts/CircleContext';
import { ThemeContextProvider, useThemeContext } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { Layout } from './components/common/Layout';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { MaintenanceGate } from './components/common/MaintenanceGate';
import { RequirePermission } from './components/common/RequirePermission';
import { UpdatePrompt } from './components/pwa/UpdatePrompt';
import { InstallPrompt } from './components/pwa/InstallPrompt';

// Pages (lazy loaded)
import { Suspense, lazy } from 'react';
import { LoadingSpinner } from './components/common/LoadingSpinner';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const ActivateDevicePage = lazy(() => import('./pages/ActivateDevicePage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage'));
const AndroidAppDownloadPage = lazy(() => import('./pages/AndroidAppDownloadPage'));
const MediaSyncPage = lazy(() => import('./pages/MediaSyncPage'));
const ProfilePage = lazy(() => import('./pages/Profile/ProfilePage'));
const SystemSettingsPage = lazy(() => import('./pages/SystemSettingsPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const MediaLibraryPage = lazy(() => import('./pages/MediaLibrary'));
const MediaMapPage = lazy(() => import('./pages/MediaMapPage'));
const CircleListPage = lazy(() => import('./pages/Circles/CircleListPage'));
const CircleDetailPage = lazy(() => import('./pages/Circles/CircleDetailPage'));
const DbBackupPage = lazy(() => import('./pages/Admin/DbBackupPage'));
const AiSettingsPage = lazy(() => import('./pages/Admin/AiSettingsPage'));
const FaceSettingsPage = lazy(() => import('./pages/Admin/FaceSettingsPage'));
const GeoSettingsPage = lazy(() => import('./pages/Admin/GeoSettingsPage'));
const EmailSettingsPage = lazy(() => import('./pages/Admin/EmailSettingsPage'));
const PushConfigPage = lazy(() => import('./pages/Admin/PushConfigPage'));
const NotificationPolicyPage = lazy(() => import('./pages/Admin/NotificationPolicyPage'));
const BroadcastsPage = lazy(() => import('./pages/Admin/BroadcastsPage'));
const AndroidAppPage = lazy(() => import('./pages/Admin/AndroidAppPage'));
const JobsPage = lazy(() => import('./pages/Admin/JobsPage'));
const JobInsightsPage = lazy(() => import('./pages/Admin/JobInsightsPage'));
const WorkersPage = lazy(() => import('./pages/Admin/WorkersPage'));
const NodeBackupPage = lazy(() => import('./pages/Admin/NodeBackupPage'));
const DoctorPage = lazy(() => import('./pages/Admin/DoctorPage'));
const WorkflowsSettingsPage = lazy(() => import('./pages/Admin/WorkflowsSettingsPage'));
const StorageInsightsPage = lazy(() => import('./pages/Admin/StorageInsightsPage'));
const StorageProvidersPage = lazy(() => import('./pages/Admin/StorageProvidersPage'));
const SettingsHubPage = lazy(() => import('./pages/Admin/SettingsHubPage'));
const TaggingSettingsPage = lazy(() => import('./pages/Admin/TaggingSettingsPage'));
const BurstsSettingsPage = lazy(() => import('./pages/Admin/BurstsSettingsPage'));
const DuplicatesSettingsPage = lazy(() => import('./pages/Admin/DuplicatesSettingsPage'));
const SocialMediaSettingsPage = lazy(() => import('./pages/Admin/SocialMediaSettingsPage'));
const EnhancerSettingsPage = lazy(() => import('./pages/Admin/EnhancerSettingsPage'));
const MemoriesSettingsPage = lazy(() => import('./pages/Admin/MemoriesSettingsPage'));
const LocationInferenceSettingsPage = lazy(() => import('./pages/Admin/LocationInferenceSettingsPage'));
const LocationGroupsSettingsPage = lazy(() => import('./pages/Admin/LocationGroupsSettingsPage'));
const ArchivingSettingsPage = lazy(() => import('./pages/Admin/ArchivingSettingsPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const PeoplePage = lazy(() => import('./pages/People/PeoplePage'));
const ArchivedFacesPage = lazy(() => import('./pages/People/ArchivedFacesPage'));
const AlbumsPage = lazy(() => import('./pages/Albums'));
const AlbumPage = lazy(() => import('./pages/Albums/AlbumPage'));
const TagsBrowsePage = lazy(() => import('./pages/Tags'));
const PlacesOverviewPage = lazy(() => import('./pages/Places'));
const LevelBrowsePage = lazy(() => import('./pages/Places/LevelBrowsePage'));
const BurstsPage = lazy(() => import('./pages/Bursts/BurstsPage'));
const BurstGroupPage = lazy(() => import('./pages/Bursts/BurstGroupPage'));
const DuplicatesPage = lazy(() => import('./pages/Duplicates/DuplicatesPage'));
const DuplicateGroupPage = lazy(() => import('./pages/Duplicates/DuplicateGroupPage'));
const LocationSuggestionsPage = lazy(() => import('./pages/LocationSuggestions/LocationSuggestionsPage'));
const LocationSuggestionRunRedirect = lazy(() => import('./pages/LocationSuggestions/LocationSuggestionRunRedirect'));
const ReviewRunPage = lazy(() => import('./pages/Reviews/ReviewRunPage'));
const ReviewHubPage = lazy(() => import('./pages/Reviews/ReviewHubPage'));
const CollectionsHubPage = lazy(() => import('./pages/Collections/CollectionsHubPage'));
const ReviewQueuePage = lazy(() => import('./pages/Reviews/ReviewQueuePage'));
const ReviewInsightsPage = lazy(() => import('./pages/Insights/ReviewInsightsPage'));
const ArchivePage = lazy(() => import('./pages/Archive/ArchivePage'));
const TrashPage = lazy(() => import('./pages/Trash/TrashPage'));
const TrashEmptyRunPage = lazy(() => import('./pages/Trash/TrashEmptyRunPage'));
const PublicSharePage = lazy(() => import('./pages/Public/PublicSharePage'));
const PublicSharesPage = lazy(() => import('./pages/Admin/PublicSharesPage'));
const WorkflowListPage = lazy(() => import('./pages/Workflows/WorkflowListPage'));
const WorkflowBuilderPage = lazy(() => import('./pages/Workflows/WorkflowBuilderPage'));
const WorkflowRunPage = lazy(() => import('./pages/Workflows/WorkflowRunPage'));
const EnhancementsPage = lazy(() => import('./pages/Enhancements/EnhancementsPage'));
const EnhancementBatchPage = lazy(() => import('./pages/Enhancements/EnhancementBatchPage'));
const NotificationsPage = lazy(() => import('./pages/Notifications/NotificationsPage'));
const MemoriesPage = lazy(() => import('./pages/Memories/MemoriesPage'));
const MemoryDetailPage = lazy(() => import('./pages/Memories/MemoryDetailPage'));

// Test login page (development only)
const TestLoginPage = import.meta.env.PROD
  ? null
  : lazy(() => import('./pages/TestLoginPage'));

function AppRoutes() {
  const { theme } = useThemeContext();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* PWA (issue #482): outside <Routes>, <ErrorBoundary> and the
          maintenance gate, so the service worker registers — and a waiting
          update can be offered — on every route, including /login, public
          share pages and the maintenance takeover (which is exactly when a
          fixed build is about to land). Both render null by default. */}
      <UpdatePrompt />
      <InstallPrompt />
      <ErrorBoundary>
        {/* Maintenance mode (issue #348): sits outside <Routes> so the takeover
            is global — every route, authenticated or not — instead of each
            page having to handle a maintenance 503 itself. */}
        <MaintenanceGate>
        <Suspense fallback={<LoadingSpinner fullScreen />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/s/:token" element={<PublicSharePage />} />

            {/* Test login (development only) */}
            {!import.meta.env.PROD && TestLoginPage && (
              <Route path="/testing/login" element={<TestLoginPage />} />
            )}

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              {/* Device activation page - without layout for full-screen experience */}
              <Route path="/activate" element={<ActivateDevicePage />} />

              <Route element={<Layout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/settings" element={<UserSettingsPage />} />
                {/* Android app + Media Sync (issue #515). Sub-routes of the
                    stacked /settings page, which links to both from its
                    "Android app" section. */}
                <Route path="/settings/android-app" element={<AndroidAppDownloadPage />} />
                <Route
                  path="/settings/media-sync"
                  element={
                    <RequirePermission permission="media:read" fallback={<Navigate to="/settings" replace />}>
                      <MediaSyncPage />
                    </RequirePermission>
                  }
                />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/media" element={<MediaLibraryPage />} />
                <Route path="/circles" element={<CircleListPage />} />
                <Route path="/circles/:id" element={<CircleDetailPage />} />
                {/* Settings Hub */}
                <Route path="/admin/settings" element={<SettingsHubPage />} />
                <Route path="/admin/settings/general" element={<SystemSettingsPage />} />
                <Route path="/admin/settings/users" element={<UserManagementPage />} />
                <Route path="/admin/settings/ai" element={<AiSettingsPage />} />
                <Route path="/admin/settings/tagging" element={<TaggingSettingsPage />} />
                <Route path="/admin/settings/face" element={<FaceSettingsPage />} />
                <Route path="/admin/settings/bursts" element={<BurstsSettingsPage />} />
                <Route path="/admin/settings/duplicates" element={<DuplicatesSettingsPage />} />
                <Route path="/admin/settings/social-media" element={<SocialMediaSettingsPage />} />
                <Route path="/admin/settings/enhancer" element={<EnhancerSettingsPage />} />
                <Route path="/admin/settings/memories" element={<MemoriesSettingsPage />} />
                <Route path="/admin/settings/location-inference" element={<LocationInferenceSettingsPage />} />
                <Route path="/admin/settings/location-groups" element={<LocationGroupsSettingsPage />} />
                <Route path="/admin/settings/archiving" element={<ArchivingSettingsPage />} />
                <Route path="/admin/settings/geo" element={<GeoSettingsPage />} />
                <Route path="/admin/settings/email" element={<EmailSettingsPage />} />
                <Route path="/admin/settings/push" element={<PushConfigPage />} />
                <Route path="/admin/settings/notifications" element={<NotificationPolicyPage />} />
                <Route path="/admin/settings/android" element={<AndroidAppPage />} />
                <Route path="/admin/settings/storage/providers" element={<StorageProvidersPage />} />
                <Route path="/admin/settings/storage/insights" element={<StorageInsightsPage />} />
                <Route path="/admin/settings/jobs" element={<JobsPage />} />
                <Route path="/admin/settings/jobs/insights" element={<JobInsightsPage />} />
                <Route path="/admin/settings/nodes" element={<WorkersPage />} />
                <Route path="/admin/settings/nodes/:id/backup" element={<NodeBackupPage />} />
                <Route path="/admin/settings/doctor" element={<DoctorPage />} />
                <Route path="/admin/settings/broadcasts" element={<BroadcastsPage />} />
                <Route path="/admin/settings/workflows" element={<WorkflowsSettingsPage />} />
                {/* v0 server-side backup retired (#367) — superseded by the node-based
                    Local Media Backup reachable from the Worker Nodes page. */}
                <Route
                  path="/admin/settings/backup"
                  element={<Navigate to="/admin/settings/nodes" replace />}
                />
                <Route path="/admin/settings/db-backup" element={<DbBackupPage />} />
                <Route path="/admin/settings/sharing" element={<PublicSharesPage />} />
                {/* Legacy admin route redirects */}
                <Route path="/admin/users" element={<Navigate to="/admin/settings/users" replace />} />
                <Route path="/admin/ai-settings" element={<Navigate to="/admin/settings/ai" replace />} />
                <Route path="/admin/face-settings" element={<Navigate to="/admin/settings/face" replace />} />
                <Route path="/admin/jobs" element={<Navigate to="/admin/settings/jobs" replace />} />
                <Route path="/admin/tags" element={<Navigate to="/admin/settings/tagging" replace />} />
                <Route path="/admin/insights" element={<Navigate to="/admin/settings/storage/insights" replace />} />
                <Route path="/admin/storage-providers" element={<Navigate to="/admin/settings/storage/providers" replace />} />
                <Route path="/admin/backup" element={<Navigate to="/admin/settings/nodes" replace />} />
                <Route path="/search" element={<SearchPage />} />
                {/* The Collections hub (issue #391, spec §4.6) ADDS a route; it
                    replaces none. Every browse destination below keeps its own
                    URL, because bookmarks, deep links, the Android app and the
                    notification links all point at them. On desktop this page
                    redirects to `/albums` rather than rendering the card grid —
                    the #392 context pane already carries the overview. */}
                <Route path="/collections" element={<CollectionsHubPage />} />
                <Route path="/people" element={<PeoplePage />} />
                <Route path="/people/archived" element={<ArchivedFacesPage />} />
                <Route path="/tags" element={<TagsBrowsePage />} />
                <Route path="/places" element={<PlacesOverviewPage />} />
                <Route path="/places/countries" element={<LevelBrowsePage level="countries" />} />
                <Route path="/places/regions" element={<LevelBrowsePage level="regions" />} />
                <Route path="/places/cities" element={<LevelBrowsePage level="cities" />} />
                <Route path="/albums" element={<AlbumsPage />} />
                <Route path="/albums/:albumId" element={<AlbumPage />} />
                <Route path="/workflows" element={<WorkflowListPage />} />
                <Route path="/workflows/new" element={<WorkflowBuilderPage />} />
                <Route path="/workflows/:id" element={<WorkflowBuilderPage />} />
                <Route path="/workflows/:id/runs/:runId" element={<WorkflowRunPage />} />
                <Route path="/enhancements" element={<EnhancementsPage />} />
                {/* Bulk AI enhance progress — a run page like the others (#423). */}
                <Route path="/enhancement-batches/:id" element={<EnhancementBatchPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                {/* Memories (epic #300). The routes always resolve — a bookmark
                    must not 404 — and each page renders its own disabled state
                    when `features.memories` is off. */}
                <Route path="/memories" element={<MemoriesPage />} />
                <Route path="/memories/:id" element={<MemoryDetailPage />} />
                {/* The Review hub (issue #390). It ADDS a namespace; it does not
                    replace one — every queue's standalone route below still
                    resolves, because bookmarks, the Home review banners and the
                    `review_queue_*` notification links all point at them. */}
                <Route path="/review" element={<ReviewHubPage />} />
                <Route path="/review/:queue" element={<ReviewQueuePage />} />
                <Route path="/bursts" element={<BurstsPage />} />
                <Route path="/bursts/:id" element={<BurstGroupPage />} />
                <Route path="/duplicates" element={<DuplicatesPage />} />
                <Route path="/duplicates/:id" element={<DuplicateGroupPage />} />
                <Route path="/review-insights" element={<ReviewInsightsPage />} />
                <Route path="/location-suggestions" element={<LocationSuggestionsPage />} />
                {/* Deprecated alias — location-suggestion runs are review runs now. */}
                <Route path="/location-suggestion-runs/:runId" element={<LocationSuggestionRunRedirect />} />
                <Route path="/review-runs/:runId" element={<ReviewRunPage />} />
                <Route path="/archive" element={<ArchivePage />} />
                <Route path="/trash" element={<TrashPage />} />
                <Route path="/trash/runs/:runId" element={<TrashEmptyRunPage />} />
              </Route>

              {/* Full-bleed layout — map owns the entire content area (no padding) */}
              <Route element={<Layout fullBleed />}>
                <Route path="/map" element={<MediaMapPage />} />
              </Route>
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </MaintenanceGate>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <ThemeContextProvider>
      <AuthProvider>
        <CircleProvider>
          <AppRoutes />
        </CircleProvider>
      </AuthProvider>
    </ThemeContextProvider>
  );
}
